import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useLocation, Link } from 'wouter';
import { Button } from '@/components/ui/button';
import { useToast } from "@/hooks/use-toast";
import { useAuthenticatedRequest } from "@/hooks/use-authenticated-request";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";

interface InvitationDetails {
  email: string;
  roleToAssign: string;
  tenantId: string | null;
  tenantName?: string | null;
  invitationToken: string;
}

// Define Zod schema for form validation
const passwordSchema = z.string()
  .min(8, { message: "Password must be at least 8 characters long" })
  .regex(/[a-z]/, { message: "Password must contain at least one lowercase letter" })
  .regex(/[A-Z]/, { message: "Password must contain at least one uppercase letter" })
  .regex(/[0-9]/, { message: "Password must contain at least one number" })
  .regex(/[^a-zA-Z0-9]/, { message: "Password must contain at least one special character" });

const acceptInvitationSchema = z.object({
  fullName: z.string().min(1, { message: "Full name is required" }),
  password: passwordSchema,
  confirmPassword: passwordSchema,
  jobTitle: z.string().optional(),
  company: z.string().optional(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"], // path to field that gets the error
});

type AcceptInvitationFormData = z.infer<typeof acceptInvitationSchema>;

export default function AcceptInvitationPage() {
  const [, _setLocation] = useLocation();
  const { supabase, fetchUserProfile } = useAuth();
  const { toast } = useToast();
  const authenticatedRequest = useAuthenticatedRequest();

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [invitationDetails, setInvitationDetails] = useState<InvitationDetails | null>(null);

  const { register, handleSubmit, formState: { errors: formErrors } } = useForm<AcceptInvitationFormData>({
    resolver: zodResolver(acceptInvitationSchema),
  });

  useEffect(() => {
    const processInvitation = async () => {
      setIsLoading(true);
      setError(null);
      setStep('loading');

      // Extracted into a separate function for clarity
      const extractInvitationToken = (): string | null => {
        // First check query params for invitation_token
        const urlParams = new URLSearchParams(window.location.search);
        const invitationToken = urlParams.get('invitation_token');
        
        if (invitationToken) {
          console.log('Found invitation token in query params:', invitationToken);
          return invitationToken;
        }
        
        // Check hash params (which Supabase might use)
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const tokenFromHash = hashParams.get('invitation_token');
        
        if (tokenFromHash) {
          console.log('Found invitation token in hash:', tokenFromHash);
          return tokenFromHash;
        }
        
        // Check if we have a token stored in localStorage (from a previous step)
        const storedToken = localStorage.getItem('invitation_token');
        if (storedToken) {
          console.log('Found stored invitation token:', storedToken);
          return storedToken;
        }
        
        console.log('No invitation token found in URL or localStorage');
        return null;
      };

      if (!supabase) {
        setError('Supabase client is not initialized. Cannot process invitation.');
        toast({ title: "Initialization Error", description: "Supabase client not available.", variant: "destructive" });
        setStep('error');
        setIsLoading(false);
        return;
      }

      // First, check if user is already authenticated with Supabase
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session || !session.user || !session.user.email) {
        // Check if there are error parameters in the URL, which might indicate auth issues
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        
        // Check for errors in either query params or hash
        const errorParam = urlParams.get('error') || hashParams.get('error');
        const errorCode = urlParams.get('error_code') || hashParams.get('error_code');
        const errorDescription = urlParams.get('error_description') || hashParams.get('error_description');
        
        if (errorParam && (errorCode === 'otp_expired' || errorParam === 'access_denied')) {
          setError(`The invitation link has expired or is invalid. ${errorDescription || 'Please request a new invitation.'}`);
          toast({
            title: "Invitation Error",
            description: "The invitation link has expired. Please contact your administrator for a new invitation.",
            variant: "destructive",
          });
          setStep('error');
          setIsLoading(false);
          return;
        }
        
        setError('No active session. Please ensure you have accepted the invitation through your email.');
        toast({
          title: "Session Error",
          description: "No active session found. Please use the link from your invitation email.",
          variant: "destructive",
        });
        setStep('error');
        setIsLoading(false);
        return;
      }

      const user = session.user;
      const userEmail = user.email;

      if (!userEmail) {
        setError('User email not found in session. Cannot process invitation.');
        toast({
          title: "Session Error",
          description: "User email is missing from the session. Please try again or contact support.",
          variant: "destructive",
        });
        setStep('error');
        setIsLoading(false);
        return;
      }

      // Extract invitation token from URL or storage
      const invitationToken = extractInvitationToken();

      if (!invitationToken) {
        setError('Invitation token not found. Please use the link from your invitation email.');
        toast({
          title: "Invitation Error",
          description: "Missing invitation token. Please use the link from your invitation email.",
          variant: "destructive",
        });
        setStep('error');
        setIsLoading(false);
        return;
      }

      // Store the token in localStorage in case we need it after a redirect
      localStorage.setItem('invitation_token', invitationToken);

      try {
        // Fetch invitation details from the server
        const response = await authenticatedRequest(`/api/invitations/details?token=${invitationToken}`);
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to retrieve invitation details');
        }
        
        const invitationData = await response.json();
        
        if (!invitationData || !invitationData.email || !invitationData.role) {
          throw new Error('Invalid invitation data received from server');
        }
        
        // Verify the email matches
        if (invitationData.email.toLowerCase() !== userEmail.toLowerCase()) {
          throw new Error('The email in the invitation does not match your account email. Please use the correct account or contact support.');
        }
        
        setInvitationDetails({
          email: userEmail,
          roleToAssign: invitationData.role,
          tenantId: invitationData.tenantId,
          tenantName: invitationData.tenantName,
          invitationToken: invitationToken
        });
        
        setStep('form');
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Failed to process invitation details';
        console.error('Error processing invitation:', errorMessage);
        
        // Fallback to using user metadata if server call fails
        const userMetadata = user.user_metadata;
        if (userMetadata && userMetadata.role && userMetadata.invitation_token === invitationToken) {
          console.log('Falling back to user metadata for invitation details');
      
      setInvitationDetails({
            email: userEmail,
        roleToAssign: userMetadata.role as string,
            tenantId: userMetadata.tenant_id ? (userMetadata.tenant_id as string) : null,
            tenantName: userMetadata.tenant_name as string,
            invitationToken: invitationToken
      });
          
      setStep('form');
        } else {
          setError(`${errorMessage}. The invitation might be expired, already used, or invalid.`);
          toast({
            title: "Invitation Error",
            description: errorMessage,
            variant: "destructive",
          });
          setStep('error');
        }
      } finally {
      setIsLoading(false);
      }
    };

    processInvitation();
  }, [toast, supabase]);

  const onSubmit = async (data: AcceptInvitationFormData) => {
    setIsLoading(true);
    setError(null);

    if (!supabase) {
      setError("Supabase client is not available. Cannot update password.");
      toast({ title: "Error", description: "Supabase client not found.", variant: "destructive" });
      setIsLoading(false);
      setStep('error');
      return;
    }

    if (!invitationDetails) {
      setError("Invitation details are missing. Cannot process request.");
      toast({ title: "Error", description: "Invitation details not found.", variant: "destructive" });
      setIsLoading(false);
      setStep('error');
      return;
    }

    try {
      // 1. Update Supabase Auth user with the new password
      const { error: updateError } = await supabase.auth.updateUser({ password: data.password });

      if (updateError) {
        console.error("Error updating user password:", updateError);
        setError(`Failed to set password: ${updateError.message}`);
        toast({
          title: "Password Update Failed",
          description: updateError.message,
          variant: "destructive",
        });
        setIsLoading(false);
        setStep('form'); // Stay on form to allow retry
        return;
      }

      // Get the access token for the API call
      const { data: { session } } = await supabase.auth.getSession();
      if (!session || !session.access_token) {
        setError("Authentication error: No active session token found.");
        toast({
          title: "Authentication Error",
          description: "Could not retrieve authentication token. Please try logging in again.",
          variant: "destructive",
        });
        setIsLoading(false);
        setStep('error');
        return;
      }

      // 2. Call the backend to finalize account setup in public.users table
      const response = await authenticatedRequest("/api/invitations/accept", {
        method: "POST",
        body: JSON.stringify({
          invitationToken: invitationDetails.invitationToken,
          fullName: data.fullName,
          password: data.password,
          jobTitle: data.jobTitle,
          company: data.company,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        console.error("Error accepting invitation:", result.message);
        setError(result.message || "An unexpected error occurred.");
        toast({
          title: "Account Setup Failed",
          description: result.message || "Could not finalize your account setup.",
          variant: "destructive",
        });
        setIsLoading(false);
        setStep('form'); // Or 'error' if it's a non-recoverable backend issue
        return;
      }

      // Clean up stored token
      localStorage.removeItem('invitation_token');

      toast({
        title: "Account Activated!",
        description: "Your account has been successfully set up. You can now log in.",
      });
      await fetchUserProfile(); // Refresh user profile which might include new activeTenantContext
      setStep('success');
      // No explicit redirect here, success message will guide user.
      // Or redirect to home: navigate("/");

    } catch (err) {
      console.error("Invitation acceptance process error:", err);
      const errorMessage = err instanceof Error ? err.message : "An unknown error occurred.";
      setError(errorMessage);
      toast({
        title: "Setup Error",
        description: errorMessage,
        variant: "destructive",
      });
      setStep('error');
    } finally {
      setIsLoading(false);
    }
  };

  if (step === 'loading') {
    return <div className="flex items-center justify-center min-h-screen">Loading invitation details...</div>;
  }

  if (step === 'error' || (!invitationDetails && step !== 'success')) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <h1 className="text-2xl font-bold text-destructive mb-4">Invitation Error</h1>
        <p className="mb-4">{error || "Could not load invitation details. The invitation might be invalid, expired, or already used."}</p>
        <p>Please ensure you used the correct link from your invitation email. If the problem persists, contact your administrator.</p>
        <Button asChild className="mt-6">
          <Link href="/auth">Go to Login</Link>
        </Button>
      </div>
    );
  }
  
  if (step === 'success') {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center">
        <h1 className="text-2xl font-bold text-green-600 mb-4">Account Activated!</h1>
        <p>Your account has been successfully set up.</p>
        <p>You can now <Link href="/auth" className="underline hover:text-indigo-500">log in</Link> with your new credentials.</p>
      </div>
    );
  }

  if (step === 'form' && invitationDetails) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="flex justify-center mb-6">
            <span className="text-2xl font-bold tracking-tight text-indigo-700">Vertrage</span>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Complete Your Account Setup
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Welcome! Please set up your account for {invitationDetails.email}.
          </p>
          {invitationDetails.tenantName && (
            <p className="mt-1 text-center text-sm text-blue-600">
              You are joining <strong>{invitationDetails.tenantName}</strong> as a <strong>{invitationDetails.roleToAssign}</strong>.
            </p>
          )}
          {invitationDetails.roleToAssign === 'SuperAdmin' && (
            <p className="mt-1 text-center text-sm text-blue-600">
              You are being set up as a <strong>System Administrator</strong> with access to all tenants.
            </p>
          )}
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="fullName"
                    type="text"
                    autoComplete="name"
                    required
                    {...register("fullName")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {formErrors.fullName && <p className="mt-2 text-sm text-red-600">{formErrors.fullName.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="passwordInput" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="passwordInput"
                    type="password"
                    autoComplete="new-password"
                    required
                    {...register("password")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {formErrors.password && <p className="mt-2 text-sm text-red-600">{formErrors.password.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    required
                    {...register("confirmPassword")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {formErrors.confirmPassword && <p className="mt-2 text-sm text-red-600">{formErrors.confirmPassword.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="jobTitle" className="block text-sm font-medium text-gray-700">
                  Job Title (Optional)
                </label>
                <div className="mt-1">
                  <input
                    id="jobTitle"
                    type="text"
                    autoComplete="organization-title"
                    {...register("jobTitle")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {formErrors.jobTitle && <p className="mt-2 text-sm text-red-600">{formErrors.jobTitle.message}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="company" className="block text-sm font-medium text-gray-700">
                  Company (Optional)
                </label>
                <div className="mt-1">
                  <input
                    id="company"
                    type="text"
                    autoComplete="organization"
                    {...register("company")}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                  />
                  {formErrors.company && <p className="mt-2 text-sm text-red-600">{formErrors.company.message}</p>}
                </div>
              </div>
              
              {error && (
                <div className="rounded-md bg-red-50 p-4 mt-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  {isLoading ? "Processing..." : "Complete Account Setup"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  return <div className="flex items-center justify-center min-h-screen">An unexpected state was reached. Please try again or contact support.</div>;
}